import { v1 as uuid } from 'uuid'

import {
  defaultOnAnswerCATINoActions,
  defaultOnAnswerTextNoActions,
  defaultOnAnswerVoiceNoActions,
} from '@/app/module/campaigns/component/item/steps/content/types/multimessage/content'
import { API_DEFAULT_RETRIES, DEFAULT_RETRIES, TIMEOUT_DEFAULT } from '@/app/module/campaigns/definitions'
import { getCampaignType } from '@/app/module/campaigns/helpers'
import { createNewReminderMessage } from '@/app/module/campaigns/models/reminder/message'
import {
  ActionMessage,
  APIMessage,
  CATIMessage,
  CATIQuestion,
  CampaignTemplateType,
  DelayState,
  PartFamily,
  QuotaActionsMessage,
  ReminderActionMessage,
  ReminderMessage,
  ReminderQuotaActionsMessage,
  ReminderSMSMessage,
  ReminderSMSQuestion,
  SMSMessage,
  SMSQuestion,
  TimeUnit,
  Message as TMessage,
  TopupMessage,
  VoiceMessage,
  VoiceQuestion,
  WhatsAppFlow,
  WhatsAppMessage,
  WhatsAppQuestion,
} from '@/app/module/campaigns/types'

import { getDefaultFailureAction } from './empty-config'

const getEmptyDelta = (timezone: string, nextIndex: number): DelayState => ({
  country: '',
  dayOfWeek: '',
  daysOfWeek: {},
  time: '',
  timezone,
  timezones: [],
  unit: !nextIndex ? TimeUnit.Immediate : TimeUnit.Day,
  value: !nextIndex ? 0 : 1,
  valueError: '',
})

export function getEmptyCatiMessage(campaignType: CampaignTemplateType): CATIMessage {
  if (campaignType === CampaignTemplateType.CATI) {
    return {
      id: uuid(),
      changed: false,
      type: PartFamily.CATI,
      hasQuestion: false,
      message: {
        text: '',
      },
    }
  }
  throw new Error(`Invalid campaign type. ${campaignType} doesn't support CATI messages`)
}

export function getEmptySMSMessage(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): SMSMessage | ReminderSMSMessage {
  switch (campaignType) {
    case CampaignTemplateType.SMSBlast:
    case CampaignTemplateType.SMSSurvey: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.SMS,
        hasQuestion: false,
        message: {
          text: '',
        },
      }
    }
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.SMS,
        hasQuestion: false,
        message: {
          text: '',
        },
        delta: getEmptyDelta(timezone, nextIndex),
      }
    }
    case CampaignTemplateType.Reminder: {
      return createNewReminderMessage({ contentType: 'sms-m', timezone })
    }
    default: {
      throw new Error(`Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support SMS messages`)
    }
  }
}

export function getEmptyCATIMessage(campaignType: CampaignTemplateType): CATIMessage {
  switch (campaignType) {
    case CampaignTemplateType.CATI: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.CATI,
        hasQuestion: false,
        message: {
          text: '',
        },
      }
    }
    default: {
      throw new Error(`Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support CATI messages`)
    }
  }
}

export function getEmptySMSQuestion(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): SMSQuestion | ReminderSMSQuestion {
  switch (campaignType) {
    case CampaignTemplateType.SMSSurvey: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.SMS,
        hasQuestion: true,
        useAi: false,
        onAnswer: defaultOnAnswerTextNoActions,
        retries: DEFAULT_RETRIES,
        onTimeout: {
          actions: [],
        },
        onInvalidReply: {
          actions: [],
        },
        onRetriesExhausted: getDefaultFailureAction(campaignType),
        openEnded: undefined,
        message: {
          text: '',
        },
      }
    }
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.SMS,
        hasQuestion: true,
        useAi: false,
        onAnswer: defaultOnAnswerTextNoActions,
        retries: DEFAULT_RETRIES,
        onTimeout: {
          actions: [],
        },
        onInvalidReply: {
          actions: [],
        },
        onRetriesExhausted: getDefaultFailureAction(campaignType),
        openEnded: undefined,
        message: {
          text: '',
        },
        delta: getEmptyDelta(timezone, nextIndex),
      }
    }
    case CampaignTemplateType.Reminder: {
      return createNewReminderMessage({ contentType: 'sms-q', timezone })
    }
    default: {
      throw new Error(`Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support SMS questions`)
    }
  }
}

export function getEmptyCATIQuestion(campaignType: CampaignTemplateType): CATIQuestion {
  switch (campaignType) {
    case CampaignTemplateType.CATI: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.CATI,
        hasQuestion: true,
        onAnswer: defaultOnAnswerCATINoActions,
        message: {
          text: '',
        },
      }
    }
    default: {
      throw new Error(
        `Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support CATI questions`,
      )
    }
  }
}

export function getEmptyVoiceMessage(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): VoiceMessage {
  switch (campaignType) {
    case CampaignTemplateType.VoiceBlast:
    case CampaignTemplateType.VoiceSurvey: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.Voice,
        hasQuestion: false,
        audio: {},
      }
    }
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.Voice,
        hasQuestion: false,
        audio: {},
        delta: getEmptyDelta(timezone, nextIndex),
      }
    }
    default: {
      throw new Error(
        `Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support voice messages`,
      )
    }
  }
}

export function getEmptyVoiceQuestion(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): VoiceQuestion {
  switch (campaignType) {
    case CampaignTemplateType.VoiceBlast:
    case CampaignTemplateType.VoiceSurvey: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.Voice,
        hasQuestion: true,
        onAnswer: defaultOnAnswerVoiceNoActions,
        keypressAfterAudio: false,
        repeatQuestion: {
          reply: '',
        },
        retries: DEFAULT_RETRIES,
        onTimeout: {
          actions: [],
        },
        onInvalidReply: {
          actions: [],
        },
        onRetriesExhausted: getDefaultFailureAction(campaignType),
        audio: {},
        useAi: false,
      }
    }
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.Voice,
        hasQuestion: true,
        onAnswer: defaultOnAnswerVoiceNoActions,
        keypressAfterAudio: false,
        repeatQuestion: {
          reply: '',
        },
        retries: DEFAULT_RETRIES,
        onTimeout: {
          actions: [],
        },
        onInvalidReply: {
          actions: [],
        },
        onRetriesExhausted: getDefaultFailureAction(campaignType),
        audio: {},
        delta: getEmptyDelta(timezone, nextIndex),
        useAi: false,
      }
    }
    default: {
      throw new Error(
        `Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support Voice questions`,
      )
    }
  }
}

export function getEmptyWhatsAppMessage(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): WhatsAppMessage {
  switch (campaignType) {
    case CampaignTemplateType.WhatsAppSurvey: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.WhatsApp,
        hasQuestion: false,
        message: {
          text: '',
        },
      }
    }
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.WhatsApp,
        hasQuestion: false,
        message: {
          text: '',
        },
        delta: getEmptyDelta(timezone, nextIndex),
      }
    }
    default: {
      throw new Error(
        `Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support Whatsapp messages`,
      )
    }
  }
}

export function getEmptyWhatsAppQuestion(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): WhatsAppQuestion {
  switch (campaignType) {
    case CampaignTemplateType.WhatsAppSurvey: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.WhatsApp,
        hasQuestion: true,
        useAi: false,
        transcribe: false,
        onAnswer: [],
        onButtons: {
          buttons: [
            {
              id: uuid(),
              actions: [],
              label: '',
              title: 'yes',
            },
            {
              id: uuid(),
              actions: [],
              label: '',
              title: 'no',
            },
          ],
        },
        retries: DEFAULT_RETRIES,
        onTimeout: {
          actions: [],
        },
        onInvalidReply: {
          actions: [],
        },
        onRetriesExhausted: getDefaultFailureAction(campaignType),
        openEnded: undefined,
        message: {
          text: '',
        },
      }
    }
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.WhatsApp,
        hasQuestion: true,
        useAi: false,
        transcribe: false,
        onAnswer: [],
        onButtons: {
          buttons: [
            {
              id: uuid(),
              actions: [],
              label: '',
              title: 'yes',
            },
            {
              id: uuid(),
              actions: [],
              label: '',
              title: 'no',
            },
          ],
        },
        retries: DEFAULT_RETRIES,
        onTimeout: {
          actions: [],
        },
        onInvalidReply: {
          actions: [],
        },
        onRetriesExhausted: getDefaultFailureAction(campaignType),
        openEnded: undefined,
        message: {
          text: '',
        },
        delta: getEmptyDelta(timezone, nextIndex),
      }
    }
    default: {
      throw new Error(
        `Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support Whatsapp questions`,
      )
    }
  }
}

export function getEmptyWhatsAppFlow(campaignType: CampaignTemplateType): WhatsAppFlow {
  const message: WhatsAppFlow = {
    id: uuid(),
    changed: false,
    type: PartFamily.WhatsAppFlow,
    hasQuestion: true,
    flow: {
      button: 'Open',
      id: uuid(),
      onAnswer: {
        actions: [],
      },
      onInvalidAnswer: {
        actions: [],
      },
      onRetriesExhausted: getDefaultFailureAction(campaignType),
      onTimeout: {
        actions: [],
        timeout: {
          unit: TimeUnit.Minute,
          value: TIMEOUT_DEFAULT,
        },
      },
      pages: [
        {
          elements: [],
          id: uuid(),
          title: '',
        },
      ],
      retries: DEFAULT_RETRIES,
      text: '',
    },
  }

  switch (campaignType) {
    case CampaignTemplateType.WhatsAppSurvey: {
      return message
    }
    default: {
      throw new Error(
        `Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support Whatsapp flows`,
      )
    }
  }
}

export function getEmptyActionsMessage(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): ActionMessage | ReminderActionMessage {
  switch (campaignType) {
    case CampaignTemplateType.CATI:
    case CampaignTemplateType.SMSSurvey:
    case CampaignTemplateType.VoiceBlast:
    case CampaignTemplateType.VoiceSurvey:
    case CampaignTemplateType.WhatsAppSurvey: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.Action,
        hasQuestion: true,
        actionsOnly: {
          actions: [],
        },
      }
    }
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.Action,
        hasQuestion: true,
        actionsOnly: {
          actions: [],
        },
        delta: getEmptyDelta(timezone, nextIndex),
      }
    }
    case CampaignTemplateType.Reminder: {
      return createNewReminderMessage({ contentType: 'actions-only', timezone })
    }
    default: {
      throw new Error(`Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support empty actions`)
    }
  }
}

export function getEmptyQuotaActionsMessage(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): QuotaActionsMessage | ReminderQuotaActionsMessage {
  if (campaignType === CampaignTemplateType.Reminder) {
    return createNewReminderMessage({ contentType: 'quota-actions', timezone })
  }

  const onExhausted =
    campaignType === CampaignTemplateType.VoiceSurvey ? [{ id: uuid(), hangup: {} }] : [{ id: uuid(), finishFlow: {} }]
  const message: QuotaActionsMessage = {
    id: uuid(),
    changed: false,
    type: PartFamily.QuotaActions,
    hasQuestion: true,
    quotaActions: {
      onExhausted,
      onOpen: [],
    },
  }

  switch (campaignType) {
    case CampaignTemplateType.VoiceSurvey:
      return message
    case CampaignTemplateType.Drip:
      return {
        ...message,
        delta: getEmptyDelta(timezone, nextIndex),
      }
    default:
      throw new Error(`Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support Quota Actions`)
  }
}

export function getEmptyTopupMessage(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): TopupMessage {
  switch (campaignType) {
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.Topup,
        hasQuestion: true,
        topup: {},
        delta: getEmptyDelta(timezone, nextIndex),
      }
    }
    default: {
      throw new Error(`Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support top-ups`)
    }
  }
}

export function getEmptyAPIMessage(
  campaignType: CampaignTemplateType,
  timezone: string,
  nextIndex: number,
): APIMessage {
  switch (campaignType) {
    case CampaignTemplateType.SMSSurvey:
    case CampaignTemplateType.WhatsAppSurvey: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.API,
        hasQuestion: true,
        apiCall: {
          method: 'GET',
          retries: API_DEFAULT_RETRIES,
          url: '',
        },
      }
    }
    case CampaignTemplateType.Drip: {
      return {
        id: uuid(),
        changed: false,
        type: PartFamily.API,
        hasQuestion: true,
        apiCall: {
          method: 'GET',
          retries: API_DEFAULT_RETRIES,
          url: '',
        },
        delta: getEmptyDelta(timezone, nextIndex),
      }
    }
    default: {
      throw new Error(`Invalid campaign type. ${getCampaignType({ type: campaignType })} doesn't support API messages`)
    }
  }
}

export function getEmptyMessage(
  campaignType: CampaignTemplateType,
  messageType: string,
  timezone: string,
  nextIndex: number,
): TMessage | ReminderMessage {
  switch (messageType) {
    case 'sms-m':
      return getEmptySMSMessage(campaignType, timezone, nextIndex)
    case 'sms-q':
      return getEmptySMSQuestion(campaignType, timezone, nextIndex)
    case 'cati-m':
      return getEmptyCATIMessage(campaignType)
    case 'cati-q':
      return getEmptyCATIQuestion(campaignType)
    case 'voice-m':
      return getEmptyVoiceMessage(campaignType, timezone, nextIndex)
    case 'voice-q':
      return getEmptyVoiceQuestion(campaignType, timezone, nextIndex)
    case 'whatsapp-m':
      return getEmptyWhatsAppMessage(campaignType, timezone, nextIndex)
    case 'whatsapp-q':
      return getEmptyWhatsAppQuestion(campaignType, timezone, nextIndex)
    case 'whatsapp-f':
      return getEmptyWhatsAppFlow(campaignType)
    case 'action-q':
      return getEmptyActionsMessage(campaignType, timezone, nextIndex)
    case 'quota-q':
      return getEmptyQuotaActionsMessage(campaignType, timezone, nextIndex)
    case 'topup-q':
      return getEmptyTopupMessage(campaignType, timezone, nextIndex)
    case 'api-q':
      return getEmptyAPIMessage(campaignType, timezone, nextIndex)
    default: {
      throw new Error(`Unsupported message type: ${messageType}.`)
    }
  }
}
